"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateUsageChartOptions = exports.generateSolarChartData = exports.generateUsageChartDataAxis = exports.generateUsageChartData = void 0;
var tslib_1 = require("tslib");
var asg_1 = require("@alinta-digital/asg");
var types_1 = require("../../types");
var usage_1 = require("./../../types/usage");
var date_fns_1 = require("date-fns");
/** function to return estimated color fill for electricity/gas/solar bars */
function estimatedColorFill(strokeColor, bgColor) {
    var shape = document.createElement('canvas');
    shape.width = 10;
    shape.height = 10;
    var c = shape.getContext('2d');
    c.strokeStyle = strokeColor;
    c.fillStyle = bgColor;
    c.fillRect(0, 0, 20, 20);
    c.beginPath();
    c.moveTo(2, 0);
    c.lineTo(10, 8);
    c.stroke();
    c.beginPath();
    c.moveTo(0, 8);
    c.lineTo(2, 10);
    c.stroke();
    return c.createPattern(shape, 'repeat');
}
function createBarGradient(context, colour1, colour2) {
    var _a;
    var dataset = context.dataset, dataIndex = context.dataIndex, chart = context.chart;
    var scaleMax = (_a = chart.scales['y']) === null || _a === void 0 ? void 0 : _a.max;
    if (!scaleMax || !chart.chartArea) {
        return chart.ctx.createLinearGradient(0, 0, 0, 0);
    }
    var currDataPoint = dataset.data[dataIndex];
    var currDataPointPercentageDelta = 1 - currDataPoint / scaleMax;
    var top = currDataPointPercentageDelta === 0 ? chart.chartArea.top : chart.chartArea.height * currDataPointPercentageDelta;
    var gradient = chart.ctx.createLinearGradient(0, top, 0, chart.chartArea.bottom);
    gradient.addColorStop(0, colour1);
    gradient.addColorStop(1, colour2);
    return gradient;
}
var ELECTRICITY_BAR_COLORS = {
    default: '#FF6100',
    gradient: {
        color0: '#FF6100',
        color1: '#FF6100',
    },
    estimated: {
        default: {
            strokeColor: '#F5B97F',
            bgColor: '#FF6100',
        },
        selected: {
            strokeColor: '#F5B97F',
            bgColor: '#F9954E',
        },
    },
};
var GAS_BAR_COLORS = {
    default: '#00AEEF',
    gradient: {
        color0: '#00AEEF',
        color1: '#00AEEF',
    },
    estimated: {
        default: {
            strokeColor: '#008CC0',
            bgColor: '#00AEEF',
        },
        selected: {
            strokeColor: '#008CC0',
            bgColor: '#00AEEF',
        },
    },
};
var SOLAR_BAR_COLORS = {
    default: '#FFD218',
    gradient: {
        color0: '#FFD218',
        color1: '#FFD218',
    },
    estimated: {
        default: {
            strokeColor: '#FFE68C',
            bgColor: '#FFD218',
        },
        selected: {
            strokeColor: '#FFE68C',
            bgColor: '#FFD218',
        },
    },
};
/** Output month as 3 digits uppercase eg. FEB  */
function fmtMonth(date, month) {
    return date
        .toLocaleString('default', {
        month: month,
    })
        .slice(0, 3)
        .toUpperCase();
}
/** Output start and end month 3 digits uppercase eg. ['FEB', 'MAR'].
 * If same month, it will output only 1 element eg. ['FEB']
 */
function getMonthsLabel(startMonth, endMonth, month) {
    if ((0, date_fns_1.isSameMonth)(startMonth, endMonth)) {
        return [fmtMonth(endMonth, month)];
    }
    return ["".concat(fmtMonth(startMonth, month), "-"), fmtMonth(endMonth, month)];
}
function generateUsageChartData(_a) {
    var usagePeriods = _a.usagePeriods, fuelType = _a.fuelType, barThickness = _a.barThickness, isSnapshotView = _a.isSnapshotView, singleLabel = _a.singleLabel, selectedIndex = _a.selectedIndex;
    var graphColors = {
        fuelBar: fuelType === types_1.FuelType.Electricity ? ELECTRICITY_BAR_COLORS : GAS_BAR_COLORS,
        solarBar: SOLAR_BAR_COLORS,
    };
    var estimated = graphColors.fuelBar.estimated;
    var estimatedDefaultBar = estimatedColorFill(estimated.default.strokeColor, estimated.default.bgColor);
    var getIsBarActualRead = function (barIndex) { var _a, _b; return ((_b = (_a = usagePeriods[barIndex]) === null || _a === void 0 ? void 0 : _a.usage) === null || _b === void 0 ? void 0 : _b.readQuality) === usage_1.ReadQuality.ACTUAL; };
    return {
        labels: usagePeriods.map(function (periodData) {
            return singleLabel
                ? fmtMonth(new Date(periodData.endOfPeriod), 'short')
                : getMonthsLabel(new Date(periodData.startOfPeriod), new Date(periodData.endOfPeriod), 'short');
        }),
        datasets: [
            {
                data: usagePeriods.map(function (periodData) { var _a; return ((_a = periodData.usage) === null || _a === void 0 ? void 0 : _a.cost) !== undefined && periodData.usage.cost >= 0 ? periodData.usage.cost : 0; }),
                backgroundColor: function (ctx) {
                    var isBarActual = getIsBarActualRead(ctx.dataIndex);
                    return isBarActual ? graphColors.fuelBar.default : estimatedDefaultBar;
                },
                hoverBackgroundColor: function (ctx) {
                    if (isSnapshotView) {
                        return;
                    }
                    return getIsBarActualRead(ctx.dataIndex)
                        ? createBarGradient(ctx, graphColors.fuelBar.gradient.color0, graphColors.fuelBar.gradient.color1)
                        : estimatedDefaultBar;
                },
                barThickness: barThickness,
                borderRadius: 3,
                barPercentage: 1.0,
            },
        ],
    };
}
exports.generateUsageChartData = generateUsageChartData;
function generateUsageChartDataAxis(usagePeriods, barThickness, singleLabel) {
    return {
        labels: usagePeriods.map(function (periodData) {
            return singleLabel
                ? fmtMonth(new Date(periodData.endOfPeriod), 'short')
                : getMonthsLabel(new Date(periodData.startOfPeriod), new Date(periodData.endOfPeriod), 'short');
        }),
        datasets: [
            {
                data: usagePeriods.map(function () { return 0; }),
                barThickness: barThickness,
                borderRadius: 3,
                barPercentage: 1,
            },
        ],
    };
}
exports.generateUsageChartDataAxis = generateUsageChartDataAxis;
function generateSolarChartData(usagePeriods, barThickness, isSnapshotView, selectedIndex) {
    var hasSolar = usagePeriods.some(function (up) { return up.solar; });
    if (!hasSolar) {
        return undefined;
    }
    var graphData = usagePeriods.map(function (periodData) { var _a; return ((_a = periodData.solar) === null || _a === void 0 ? void 0 : _a.cost) !== undefined && periodData.solar.cost >= 0 ? periodData.solar.cost : 0; });
    var estimated = SOLAR_BAR_COLORS.estimated;
    var getIsBarActualRead = function (barIndex) { var _a, _b; return ((_b = (_a = usagePeriods[barIndex]) === null || _a === void 0 ? void 0 : _a.solar) === null || _b === void 0 ? void 0 : _b.readQuality) === usage_1.ReadQuality.ACTUAL; };
    var estimatedDefaultBar = estimatedColorFill(estimated.default.strokeColor, estimated.default.bgColor);
    return {
        labels: usagePeriods.map(function () { return ''; }),
        datasets: [
            {
                data: graphData,
                backgroundColor: function (ctx) {
                    var isBarActual = getIsBarActualRead(ctx.dataIndex);
                    return isBarActual ? SOLAR_BAR_COLORS.default : estimatedDefaultBar;
                },
                hoverBackgroundColor: function (ctx) {
                    if (isSnapshotView) {
                        return;
                    }
                    return getIsBarActualRead(ctx.dataIndex)
                        ? createBarGradient(ctx, SOLAR_BAR_COLORS.gradient.color1, SOLAR_BAR_COLORS.gradient.color0)
                        : estimatedDefaultBar;
                },
                barThickness: barThickness,
                borderRadius: 3,
                barPercentage: 1.0,
            },
        ],
    };
}
exports.generateSolarChartData = generateSolarChartData;
function generateUsageChartOptions(opts) {
    return {
        animation: {
            duration: 0,
        },
        transitions: {
            show: {
                animations: {
                    x: {
                        from: 0,
                    },
                    y: {
                        from: 0,
                    },
                },
            },
            hide: {
                animations: {
                    x: {
                        to: 0,
                    },
                    y: {
                        to: 0,
                    },
                },
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: opts.isXAxis ? 0 : opts.selectable ? opts.paddingTop : 1,
                right: 20,
                bottom: opts.isSolar ? (opts.selectable ? 30 : 1) : 1,
            },
        },
        scales: {
            x: {
                display: !opts.isSolar,
                grid: {
                    display: false,
                    drawBorder: false,
                    tickLength: 0,
                },
                ticks: {
                    display: opts.isXAxis,
                    color: function (ctx) { return (ctx.index === opts.selectedIndex ? asg_1.colors.primaryText : '#68727C'); },
                    font: {
                        size: 15,
                        weight: function (ctx) { return (ctx.index === opts.selectedIndex && opts.selectable ? '700' : '500'); },
                        family: function (ctx) { return (ctx.index === opts.selectedIndex && opts.selectable ? 'stevie-sans' : 'stevie-sans'); },
                    },
                    padding: 2,
                    // crossAlign: 'far',
                },
                afterFit: function (axis) {
                    if (!opts.isXAxis) {
                        return;
                    }
                    if (opts.isBasic) {
                        axis.height = 40;
                        axis.maxHeight = 40;
                    }
                    else {
                        axis.height = 20;
                        axis.maxHeight = 20;
                    }
                },
            },
            y: tslib_1.__assign(tslib_1.__assign({ display: !opts.isXAxis, reverse: opts.isSolar, position: 'left' }, (opts.maxY && { max: opts.maxY })), { ticks: {
                    display: false,
                    callback: function () { return ''; },
                    maxTicksLimit: opts.isHalf ? 2 : 3,
                }, grid: {
                    borderDash: [2, 2],
                    drawBorder: false,
                }, afterFit: function (axis) {
                    axis.width = 0;
                }, afterBuildTicks: function (axis) {
                    axis.ticks = opts.isHalf
                        ? [{ value: 0 }, { value: opts.maxY }]
                        : [{ value: 0 }, { value: opts.maxY / 2 }, { value: opts.maxY }];
                } }),
        },
        interaction: {
            axis: 'x',
        },
        plugins: {
            legend: {
                display: false,
                title: {
                    padding: 0,
                },
            },
            tooltip: {
                enabled: !opts.isXAxis,
                callbacks: {
                    title: function () { return ''; },
                    label: function (tooltipItem) {
                        return isNaN(Number(tooltipItem.formattedValue))
                            ? "$".concat(tooltipItem.formattedValue)
                            : "$".concat(Number(tooltipItem.formattedValue).toFixed(2));
                    },
                },
            },
            chartAreaHighlighter: {
                activeIndex: opts.selectedIndex,
                noOfColumns: opts.numOfColumns,
                disablePlugin: !opts.selectable,
                isSolar: opts.isSolar,
                hasSolar: opts.hasSolar,
            },
            yearChangeLineLabel: {
                usagePeriods: opts.usagePeriods,
            },
            chartLabels: {
                usagePeriods: opts.usagePeriods,
                activeUsagePeriodIndex: opts.selectedIndex,
                onUsagePeriodChange: opts.onUsagePeriodChange,
            },
        },
    };
}
exports.generateUsageChartOptions = generateUsageChartOptions;
//# sourceMappingURL=UsageGraph.utilites.js.map